import { RootEpic } from 'MyTypes';
import { isEmpty } from 'lodash/fp';
import { defer, empty, forkJoin, from, of } from 'rxjs';
import {
    bufferCount,
    catchError,
    filter,
    ignoreElements,
    map,
    mergeMap,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { isActionOf, isOfType } from 'typesafe-actions';

import { getDataTypePrimaryEntityKey } from '@portal/common/models/data-type';
import { mergeGranularity } from '@portal/common/utility/merge-granularity';

import { getFiltersWithConditions } from '../utility/data-loader';
import { getStandardTrackingParams } from './analytics-selectors';
import {
    downloadDataExportAsync,
    downloadFileAsync,
    getAgeGroupsAsync,
    getCountryLocationsSdi,
    getDataCollectionsAsync,
    getRoundsAsync,
    queryDataAsync,
    queryDataRendered,
    sendPdfReportAsync,
    trackPdfDownload,
} from './data-explorer/actions';
import {
    getDataToolConfig,
    getSelectedConditions,
    getSelectedConditionsDataTypes,
    getSelectedConditionsDataTypesDefaultFilters,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    getVisibleRefinementFilters,
    mergeFiltersWithFallbackToDefaultFilters,
} from './data-explorer/selectors';
import { normalizeQueryParams } from './data-explorer/utils';
import {
    getCausesAsync,
    getCrosAsync,
    getEtiologiesAsync,
    getImpairmentsAsync,
    getInjuryAsync,
    getLocationsAsync,
    getRacesAsync,
    getRisksAsync,
    getSequelasAsync,
    getVehssAsync,
} from './entity-hierarchies/actions';
import { getOrganizationAsync, organizationContacted } from './organization/actions';
import {
    getFavoriteResourcesAsync,
    getGroupContentResourcesAsync,
    getPermalinkResourcesAsync,
    getPublicResourceListsAsync,
    getResourceListsAsync,
    removePermalinkResourceAsync,
} from './resource-lists/actions';
import { getAnnouncementsAsync, getSidebarLinksAsync } from './resources/actions';
import { updateLocation } from './router/actions';
import { getIsImpersonatedSession, getIsStealthUser, getSessionAccount } from './session/selectors';
import { SESSION_ACCOUNT_LOADED, SESSION_SIGNED_IN } from './session/types';
import { getSelectedDataCollectionForDataTool } from './user-settings/selectors';

// triggers are modelling dependencies between store modules
export const triggerLoadDataCollections: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isOfType([SESSION_ACCOUNT_LOADED, SESSION_SIGNED_IN])),
        map(() => getDataCollectionsAsync.request())
    );

export const triggerLoadOrganization: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isOfType([SESSION_ACCOUNT_LOADED, SESSION_SIGNED_IN])),
        map(() => getOrganizationAsync.request())
    );

export const triggerBootUpDictionaries: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isOfType([SESSION_ACCOUNT_LOADED, SESSION_SIGNED_IN])),
        switchMap((action) =>
            of(
                getAgeGroupsAsync.request(),
                getCountryLocationsSdi.request(),
                getRoundsAsync.request()
            )
        )
    );

export const triggerLoadEntityHierarchies: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isOfType([SESSION_ACCOUNT_LOADED, SESSION_SIGNED_IN])),
        switchMap((action) =>
            of(
                getLocationsAsync.request(),
                getCausesAsync.request(),
                getSequelasAsync.request(),
                getEtiologiesAsync.request(),
                getImpairmentsAsync.request(),
                getInjuryAsync.request(),
                getRisksAsync.request(),
                getVehssAsync.request(),
                getCrosAsync.request(),
                getRacesAsync.request()
            )
        )
    );

export const triggerLoadResourceLists: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isOfType([SESSION_ACCOUNT_LOADED, SESSION_SIGNED_IN])),
        switchMap(() =>
            of(
                getFavoriteResourcesAsync.request(),
                getPermalinkResourcesAsync.request(),
                getGroupContentResourcesAsync.request(),
                getResourceListsAsync.request(),
                getPublicResourceListsAsync.request()
            )
        )
    );

export const triggerLoadPermalinkLists: RootEpic = (action$, state$) => {
    return action$.pipe(
        filter(isActionOf([removePermalinkResourceAsync.success])),
        switchMap(() => of(getPermalinkResourcesAsync.request()))
    );
};

export const triggerLoadResources: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isOfType([SESSION_ACCOUNT_LOADED, SESSION_SIGNED_IN])),
        switchMap((action) => of(getAnnouncementsAsync.request(), getSidebarLinksAsync.request()))
    );

// Analytics
const filterOutStealthAndImpersonatedUsers = (state$) =>
    switchMap((action: any) => {
        const state = state$.value;
        const isStealthUser = getIsStealthUser(state);
        const isImpersonatedSession = getIsImpersonatedSession(state);

        if (isStealthUser || isImpersonatedSession) {
            return empty();
        }

        return of(action);
    });

export const trackVisit: RootEpic = (action$, state$, dependencies) => {
    const { analyticsService } = dependencies;
    return action$.pipe(
        filter(isOfType([SESSION_ACCOUNT_LOADED, SESSION_SIGNED_IN])),
        filterOutStealthAndImpersonatedUsers(state$),
        tap(() => {
            analyticsService.logVisit(getStandardTrackingParams(state$.value)).catch(console.log);
        }),
        ignoreElements()
    );
};

export const trackPageView: RootEpic = (action$, state$, dependencies) => {
    const { analyticsService } = dependencies;
    return action$.pipe(
        filter(isActionOf(updateLocation)),
        filterOutStealthAndImpersonatedUsers(state$),
        bufferCount(2, 1),
        tap(([prevAction]) => {
            const state = state$.value;
            const account = getSessionAccount(state);
            if (account) {
                analyticsService
                    .logPageView(getStandardTrackingParams(state), {
                        prev_app_path: prevAction.payload.pathname,
                    })
                    .catch(console.log);
            }
        }),
        ignoreElements()
    );
};

export const trackDataQuery: RootEpic = (action$, state$, dependencies) => {
    const { analyticsService } = dependencies;
    return action$.pipe(
        filter(isActionOf([queryDataAsync.request])),
        filterOutStealthAndImpersonatedUsers(state$),
        switchMap((action) => {
            const state = state$.value;

            const dataCollection = getSelectedDataCollectionForDataTool(state);

            if (isEmpty(dataCollection)) {
                throw Error('Tracking should not be invoked with no data collection selected');
            }

            const conditions = getSelectedConditions(state);
            const filters = getSelectedRefinementFiltersWithFallbackToDefaultFilters(state);

            const queryParams = normalizeQueryParams(dataCollection, conditions, filters) || [];
            const dataTypes = queryParams.map(({ dataType }) => dataType);
            const granularities = queryParams.map(({ filters }) => filters);

            if (isEmpty(dataTypes)) {
                throw Error('Tracking should not be invoked with no data types selected');
            }

            const startTimestamp = Date.now();

            const queried_data_types = dataTypes;
            const queried_granularity = mergeGranularity(...granularities);

            const resource_id = dataCollection.id;
            const visible_filters = getVisibleRefinementFilters(state);

            const successTimestamp$ = action$.pipe(
                filter(isActionOf([queryDataAsync.success])),
                take(1),
                map((action) => {
                    return action.meta;
                })
            );

            const renderedTimestamp$ = action$.pipe(
                filter(isActionOf(queryDataRendered)),
                take(1),
                map((action) => {
                    return action.payload;
                })
            );

            return forkJoin(renderedTimestamp$, successTimestamp$).pipe(
                takeUntil(
                    action$.pipe(
                        filter(isActionOf([queryDataAsync.failure, queryDataAsync.request]))
                    )
                ),
                map(([renderedTimestamp, successTimestamp]) => {
                    const api_request_time = successTimestamp - startTimestamp;
                    const render_time = renderedTimestamp - successTimestamp;

                    return {
                        api_request_time,
                        queried_data_types,
                        queried_granularity,
                        render_time,
                        resource_id,
                        source: action.payload,
                        visible_filters,
                    };
                })
            );
        }),
        tap(({ source, ...data }) => {
            analyticsService
                .logDataQuery({ ...getStandardTrackingParams(state$.value), source }, data)
                .catch(console.log);
        }),
        ignoreElements()
    );
};

export const trackDataExports: RootEpic = (action$, state$, dependencies) => {
    const { analyticsService } = dependencies;
    return action$.pipe(
        filter(isActionOf([downloadDataExportAsync.request])),
        filterOutStealthAndImpersonatedUsers(state$),
        switchMap((action) => {
            const state = state$.value;

            const selectedConditions = action.payload.conditions || getSelectedConditions(state);
            const selectedConditionsDataTypes =
                action.payload.data_types || getSelectedConditionsDataTypes(state);
            if (selectedConditionsDataTypes == null) {
                throw Error('Tracking should not be invoked with no data types selected');
            }

            const selectedDataCollection = getSelectedDataCollectionForDataTool(state);
            const queried_data_types = selectedConditionsDataTypes;
            let filters = {};
            if (action.payload.filters) {
                const defaultFilters = getSelectedConditionsDataTypesDefaultFilters(state);
                const { filtersSkipDefaultSelection } = getDataToolConfig(state);
                filters = mergeFiltersWithFallbackToDefaultFilters(
                    defaultFilters,
                    action.payload.filters,
                    filtersSkipDefaultSelection
                );
            } else {
                filters = getSelectedRefinementFiltersWithFallbackToDefaultFilters(state);
            }

            const filtersKeys = Object.keys(filters);
            const primaryEntityKeys = selectedConditionsDataTypes.map(getDataTypePrimaryEntityKey);

            const queried_granularity = getFiltersWithConditions(selectedConditions, filters, [
                ...filtersKeys,
                ...primaryEntityKeys,
            ]);

            const resource_id = selectedDataCollection.id;
            const visible_filters = getVisibleRefinementFilters(state);

            const chart_types = action.payload.chart_types; // from data explorer scene which knows what it display
            const format = action.payload.format; // from pdf export modal as action param

            return of({
                chart_types,
                format,
                queried_data_types,
                queried_granularity,
                resource_id,
                visible_filters,
            });
        }),
        tap((data) => {
            analyticsService
                .logDataExport(getStandardTrackingParams(state$.value), data)
                .catch(console.log);
        }),
        ignoreElements()
    );
};

export const trackPdfExports: RootEpic = (action$, state$, dependencies) => {
    const { analyticsService } = dependencies;
    return action$.pipe(
        filter(isActionOf([trackPdfDownload, sendPdfReportAsync.success])),
        filterOutStealthAndImpersonatedUsers(state$),
        switchMap((action) => {
            const state = state$.value;

            const selectedConditions = getSelectedConditions(state);
            const selectedConditionsDataTypes = getSelectedConditionsDataTypes(state);
            if (selectedConditionsDataTypes == null) {
                throw Error('Tracking should not be invoked with no data types selected');
            }

            const selectedDataCollection = getSelectedDataCollectionForDataTool(state);
            const queried_data_types = selectedConditionsDataTypes;
            const filters = getSelectedRefinementFiltersWithFallbackToDefaultFilters(state);
            const filtersKeys = Object.keys(filters);
            const primaryEntityKeys = selectedConditionsDataTypes.map(getDataTypePrimaryEntityKey);

            const queried_granularity = getFiltersWithConditions(selectedConditions, filters, [
                ...filtersKeys,
                ...primaryEntityKeys,
            ]);

            const resource_id = selectedDataCollection.id;
            const visible_filters = getVisibleRefinementFilters(state);

            const chart_types = action.payload.chart_types;
            const pdf_report_options = action.payload.pdf_report_options;

            return of({
                isReportEmail: isActionOf(sendPdfReportAsync.success, action),
                chart_types,
                pdf_report_options,
                queried_data_types,
                queried_granularity,
                resource_id,
                visible_filters,
            });
        }),
        tap(({ isReportEmail, ...data }) => {
            const logEvent = isReportEmail
                ? analyticsService.logPdfReport
                : analyticsService.logPdfExport;

            logEvent(getStandardTrackingParams(state$.value), data).catch(console.log);
        }),
        ignoreElements()
    );
};

// Sends the report PDF; mergeMap so an in-flight send is never cancelled.
export const sendPdfReport: RootEpic = (action$, state$, { api }) =>
    action$.pipe(
        filter(isActionOf(sendPdfReportAsync.request)),
        mergeMap(({ payload: { file, email, fileName, tracking } }) =>
            // defer so a synchronous throw becomes a send failure instead of killing the epic.
            defer(() => from(api.pdfReport.send({ email, file, fileName }))).pipe(
                map(() => sendPdfReportAsync.success(tracking)),
                catchError((error: Error) => of(sendPdfReportAsync.failure(error)))
            )
        )
    );

export const trackFileDownload: RootEpic = (action$, state$, dependencies) => {
    const { analyticsService } = dependencies;
    return action$.pipe(
        filter(isActionOf([downloadFileAsync.request])),
        filterOutStealthAndImpersonatedUsers(state$),
        mergeMap((requestAction) => {
            const startTimestamp = Date.now();

            return action$.pipe(
                takeUntil(
                    action$.pipe(
                        filter(isActionOf(downloadFileAsync.failure)),
                        filter((a) => a.payload.id === requestAction.payload.id)
                    )
                ),
                filter(isActionOf([downloadFileAsync.success])),
                filter((a) => a.payload.id === requestAction.payload.id),
                take(1),
                map((successAction) => {
                    const finishTimestamp = Date.now();
                    const download_time = finishTimestamp - startTimestamp;

                    return {
                        download_time,
                        resource_id: successAction.payload.resource_id,
                    };
                })
            );
        }),
        tap((data) => {
            analyticsService
                .logFileDownload(getStandardTrackingParams(state$.value), data)
                .catch(console.log);
        }),
        ignoreElements()
    );
};

export const trackTicketCreate: RootEpic = (action$, state$, dependencies) => {
    const { analyticsService } = dependencies;
    return action$.pipe(
        filter(isActionOf(organizationContacted)),
        filterOutStealthAndImpersonatedUsers(state$),
        switchMap((action) => {
            const reason = action.payload.reason;
            const status = action.payload.status;
            const is_manager_copied = action.payload.is_manager_copied;

            return of({
                reason,
                status,
                is_manager_copied,
            });
        }),
        tap((data) => {
            analyticsService
                .logTicketCreate(getStandardTrackingParams(state$.value), {
                    ...data,
                    status: 'open',
                })
                .catch(console.log);
        }),
        ignoreElements()
    );
};
